import {Localized, useLocalization} from "@fluent/react";
import {
	Box,
	Collapse,
	IconButton,
	LinearProgress,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import type {LinearProgressProps} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import React, {useEffect, useMemo, useRef, useState} from "react";

import ContentItemTitle from "../analytics/content/ContentItemTitle";
import ContentItemTitlePlaceholder from "../analytics/content/ContentItemTitlePlaceholder";
import Widget from "../analytics/widget/Widget";
import WidgetBody from "../analytics/widget/WidgetBody";
import WidgetBodyOverlay from "../analytics/widget/WidgetBodyOverlay";
import WidgetTitle from "../analytics/widget/WidgetTitle";
import type ContentItem from "./ContentItem";
import ContentIssueTypeFilter from "./ContentIssueTypeFilter";
import {getExerciseTypeDescription} from "../exerciseBank/exerciseTypeDescriptions";
import {resolvePath} from "../../helpers/pathHelpers";
import IssueReportsView from "./IssueReportsView";
import type ResolutionStatus from "./ResolutionStatus";
import ResolutionStatusFilter from "./ResolutionStatusFilter";
import selectChapterExercise from "../../store/chapterExercises/selectChapterExercise";
import {requiresExplicitSessionStart} from "../../store/chapters/Chapter";
import {getChapterContent} from "../../store/chapters/getChapterContent";
import selectChapter from "../../store/chapters/selectChapter";
import selectChapterContentStatus from "../../store/chapters/selectChapterContentStatus";
import selectChapterSectionKeys from "../../store/chapters/selectChapterSectionKeys";
import selectCourseChapterKeys from "../../store/chapters/selectCourseChapterKeys";
import selectCourseChaptersFetchStatus from "../../store/chapters/selectCourseChaptersFetchStatus";
import type ContentIssueType from "../../store/courses/ContentIssueType";
import CourseType from "../../store/courses/CourseType";
import selectCourse from "../../store/courses/selectCourse";
import type FetchStatus from "../../store/FetchStatus";
import {keyProvider} from "../../store/keyProvider";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import makeSelectSection from "../../store/sections/makeSelectSection";
import selectSubsectionIds from "../../store/sections/selectSubsectionIds";
import contentIssueService from "../../store/services/contentIssueService";
import type {ExerciseContentIssueStats} from "../../store/services/contentIssueService";
import AxisBar from "../../utils/AxisBar";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";
import randomN from "../../utils/randomN";
import TableCell from "../../utils/tables/Cell";
import createActionColumnDef from "../../utils/tables/createActionColumnDef";
import type {ColumnDefs} from "../../utils/tables/Head";
import LoadingErrorState from "../../utils/tables/LoadingErrorState";

const emptyItem: ContentItem = {
	id: 0,
	chapterId: 0,
	title: "",
	contentPath: "",
	subtitle: "",
};

function ContentIssuesWidget(props: {
	courseId: number;
	onRefetchChapters: () => void;
}) {
	const [selectedContentItem, setSelectedContentItem] = useState(emptyItem);

	const [selectedTypes, setSelectedTypes] = useState<ContentIssueType[]>([]);

	const [selectedStatuses, setSelectedStatuses] = useState<ResolutionStatus[]>(
		[]
	);

	const {l10n} = useLocalization();

	return (
		<Widget id="content-issue-report">
			<WidgetTitle>
				<Localized id="content-issues-widget-title">Content issues</Localized>
			</WidgetTitle>
			<WidgetBody style={{position: "relative", overflow: "auto"}}>
				<ContentIssueReport
					courseId={props.courseId}
					onOpenMessages={(item, filters) => {
						setSelectedContentItem(item);
						setSelectedTypes(filters.type);
						setSelectedStatuses(filters.resolutionStatus);
					}}
					onRefetchChapters={props.onRefetchChapters}
				/>

				<WidgetBodyOverlay in={Boolean(selectedContentItem.id)}>
					<IssueReportsView
						courseId={props.courseId}
						contentItem={selectedContentItem}
						filters={{
							type: selectedTypes,
							resolutionStatus: selectedStatuses,
						}}
						closeAction={
							<IconButtonWithTooltip
								aria-label={l10n.getString(
									"content-issues-widget-action-close-issue-view"
								)}
								tooltipTitle={
									<Localized id="content-issues-widget-action-close-issue-view">
										Close issue view
									</Localized>
								}
								onClick={() => setSelectedContentItem(emptyItem)}
							>
								<FullscreenExitIcon />
							</IconButtonWithTooltip>
						}
					/>
				</WidgetBodyOverlay>
			</WidgetBody>
		</Widget>
	);
}

const columnKeys = ["contentItem", "issues", "bar"] as const;

const columnDefs: ColumnDefs<typeof columnKeys[number]> = {
	contentItem: {
		name: (
			<Localized id="content-issues-widget-column-content-item">
				Content item
			</Localized>
		),
		width: 0.6,
	},
	issues: {
		name: (
			<Localized id="content-issues-widget-column-issues">Issues</Localized>
		),
		width: 0.1,
		align: "right",
	},
	bar: {
		name: "",
		width: 0.4,
	},
};

const actionColumn = createActionColumnDef();

const columnNumber = columnKeys.length + 1;

const defaultMaxNumberOfIssues = 10;

type ChapterStats = {
	stats: ExerciseContentIssueStats[];
	numberOfIssues: number;
};

function ContentIssueReport(props: {
	courseId: number;
	onOpenMessages: (
		contentItem: ContentItem,
		filters: {
			type: ContentIssueType[];
			resolutionStatus: ResolutionStatus[];
		}
	) => void;
	onRefetchChapters: () => void;
}) {
	const {courseId} = props;

	const [courseStats, setCourseStats] = useState<{
		[chapterKey: string]: ChapterStats | undefined;
	}>({});

	const chapters = useAppSelector((state) =>
		selectCourseChapterKeys(state, keyProvider.course(courseId))
	);

	const chapterFetchStatus = useAppSelector((state) =>
		selectCourseChaptersFetchStatus(state, keyProvider.course(courseId))
	);

	const courseType = useAppSelector(
		(state) =>
			selectCourse(state, keyProvider.course(courseId))?.type ||
			CourseType.Ordinary
	);

	const [statsFetchStatus, setStatsFetchStatus] = useState<FetchStatus>("none");

	const [selectedTypes, setSelectedTypes] = useState<ContentIssueType[]>([]);

	const [selectedStatuses, setSelectedStatuses] = useState<ResolutionStatus[]>([
		"not_resolved",
	]);

	const delayed = useRef<ReturnType<typeof setTimeout>>();

	async function fetchCourseStats(
		courseId: number,
		criteria?: {
			type?: ContentIssueType[];
			resolutionStatus?: ResolutionStatus[];
		}
	) {
		setStatsFetchStatus("pending");

		try {
			const stats = await contentIssueService.getStatisticsOnContentIssues(
				courseId,
				criteria ?? {}
			);

			const statsByCh: {[chapterKey: string]: ChapterStats} = {};
			stats.forEach((s) => {
				const key = keyProvider.chapter(s.chapterId);
				statsByCh[key] = {stats: s.stats, numberOfIssues: sum(s.stats)};
			});

			setCourseStats(statsByCh);
			setStatsFetchStatus("succeeded");
		} catch {
			setStatsFetchStatus("failed");
		}
	}

	const statsShouldBeFetched = statsFetchStatus === "none";

	useEffect(() => {
		if (statsShouldBeFetched) {
			fetchCourseStats(courseId, {resolutionStatus: ["not_resolved"]});
		}
	}, [courseId, statsShouldBeFetched]);

	const [PercentageBar, maxNumberOfIssues] = useMemo(() => {
		const maxNumberOfIssues = Math.max(
			defaultMaxNumberOfIssues,
			...Object.values(courseStats).map((s) => s?.numberOfIssues ?? 0)
		);

		return [createPercentageBar(maxNumberOfIssues), maxNumberOfIssues];
	}, [courseStats]);

	const fetchFailed =
		chapterFetchStatus === "failed" || statsFetchStatus === "failed";

	return (
		<Stack>
			<Stack spacing={1} sx={{mb: 2}}>
				<ResolutionStatusFilter
					value={selectedStatuses}
					onChange={(val) => {
						setSelectedStatuses(val);

						clearTimeout(delayed.current);

						delayed.current = setTimeout(() => {
							fetchCourseStats(courseId, {
								resolutionStatus: val,
								type: selectedTypes,
							});
							clearTimeout(delayed.current);
						}, 500);
					}}
				/>

				<ContentIssueTypeFilter
					value={selectedTypes}
					onChange={(val) => {
						setSelectedTypes(val);

						clearTimeout(delayed.current);

						delayed.current = setTimeout(() => {
							fetchCourseStats(courseId, {
								type: val,
								resolutionStatus: selectedStatuses,
							});
							clearTimeout(delayed.current);
						}, 500);
					}}
				/>
			</Stack>

			<TableContainer sx={{minHeight: fetchFailed ? undefined : 550}}>
				<Table sx={{tableLayout: "fixed", minWidth: 600}}>
					<TableHead>
						<TableRow>
							<TableCell def={columnDefs.contentItem}>
								{columnDefs.contentItem.name}
							</TableCell>
							<TableCell def={columnDefs.issues}>
								{columnDefs.issues.name}
							</TableCell>
							<TableCell def={columnDefs.bar}>
								<AxisBar min={0} max={maxNumberOfIssues} />
							</TableCell>
							<TableCell def={actionColumn} />
						</TableRow>
					</TableHead>

					<TableBody>
						{chapterFetchStatus === "pending" && (
							<>
								<ReportRowPlaceholder />
								<ReportRowPlaceholder />
								<ReportRowPlaceholder />
							</>
						)}

						{chapterFetchStatus === "failed" && (
							<LoadingErrorState
								description={
									<Localized id="content-issues-widget-error-fetch-chapters">
										Something has gone wrong, and we cannot load chapters
									</Localized>
								}
								columnNumber={columnNumber}
								onReload={props.onRefetchChapters}
							/>
						)}

						{chapterFetchStatus === "succeeded" &&
							statsFetchStatus === "failed" && (
								<LoadingErrorState
									description={
										<Localized id="content-issues-widget-error-fetch-course-issues">
											Something has gone wrong, and we cannot load course
											content issues
										</Localized>
									}
									columnNumber={columnNumber}
									onReload={() =>
										fetchCourseStats(courseId, {type: selectedTypes})
									}
								/>
							)}

						{chapterFetchStatus === "succeeded" &&
							statsFetchStatus !== "failed" &&
							chapters.map((key) => (
								<ChapterIssuesRow
									key={key}
									courseId={courseId}
									courseType={courseType}
									chapterKey={key}
									numberOfIssues={courseStats[key]?.numberOfIssues ?? 0}
									stats={courseStats[key]?.stats ?? []}
									loadingStats={
										statsFetchStatus === "none" ||
										statsFetchStatus === "pending"
									}
									PercentageBar={PercentageBar}
									onOpenMessages={(item) =>
										props.onOpenMessages(item, {
											type: selectedTypes,
											resolutionStatus: selectedStatuses,
										})
									}
								/>
							))}
					</TableBody>
				</Table>
			</TableContainer>
		</Stack>
	);
}

function sum(stats: {numberOfIssues: number}[]) {
	return stats.reduce((sum, st) => sum + st.numberOfIssues, 0);
}

function ChapterIssuesRow(props: {
	courseId: number;
	courseType: CourseType;
	chapterKey: string;

	numberOfIssues: number;
	stats: ExerciseContentIssueStats[];

	loadingStats?: boolean;

	PercentageBar: React.ComponentType<LinearProgressProps>;

	onOpenMessages: (contentItem: ContentItem) => void;
}) {
	const {chapterKey, numberOfIssues, stats} = props;

	const chapter = useAppSelector((state) => selectChapter(state, chapterKey));

	const [expanded, setExpanded] = useState(false);

	const {l10n} = useLocalization();

	if (!chapter) {
		return null;
	}

	const chapterPath = `/courses/${props.courseId}/management/content/chapters/${chapter.id}`;

	const title = (
		<ContentItemTitle
			primary={`${chapter.number} ${chapter.title}`}
			secondary={
				requiresExplicitSessionStart(props.courseType, chapter) ? (
					<Localized id="content-issues-widget-content-item-type-test">
						Test
					</Localized>
				) : (
					<Localized id="content-issues-widget-content-item-type-chapter">
						Chapter
					</Localized>
				)
			}
			link={chapterPath}
		/>
	);

	if (props.loadingStats) {
		return <ReportRowPlaceholder title={title} />;
	}

	return (
		<>
			<ReportRow
				contentTitle={title}
				numberOfIssues={numberOfIssues}
				bar={<props.PercentageBar value={numberOfIssues} />}
				action={
					Boolean(numberOfIssues) && (
						<IconButton
							aria-label={
								expanded
									? l10n.getString(
											"content-issues-widget-action-collapse-chapter"
									  )
									: l10n.getString(
											"content-issues-widget-action-expand-chapter"
									  )
							}
							onClick={() => setExpanded((prev) => !prev)}
						>
							{expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
						</IconButton>
					)
				}
			/>

			{Boolean(numberOfIssues) && (
				<TableRow>
					<TableCell
						colSpan={columnNumber}
						sx={{padding: 0, borderBottom: "unset"}}
					>
						<Collapse in={expanded} unmountOnExit>
							<ChapterContentRows
								courseId={props.courseId}
								chapterId={chapter.id}
								chapterPath={chapterPath}
								chapterNumber={chapter.number}
								chapterKey={chapterKey}
								stats={stats}
								PercentageBar={props.PercentageBar}
								onOpenMessages={props.onOpenMessages}
							/>
						</Collapse>
					</TableCell>
				</TableRow>
			)}
		</>
	);
}

function ChapterContentRows(props: {
	courseId: number;
	chapterId: number;
	chapterPath: string;
	chapterNumber: number;
	chapterKey: string;

	stats: ExerciseContentIssueStats[];

	PercentageBar: React.ComponentType<LinearProgressProps>;

	onOpenMessages: (contentItem: ContentItem) => void;
}) {
	const {courseId, chapterId, chapterKey, stats} = props;

	const sectionKeys = useAppSelector((state) =>
		selectChapterSectionKeys(state, props.chapterKey)
	);

	const contentFetchStatus = useAppSelector((state) =>
		selectChapterContentStatus(state, chapterKey)
	);

	const dispatch = useAppDispatch();

	const shouldRequestContent = contentFetchStatus === "none";

	useEffect(() => {
		if (shouldRequestContent && chapterId) {
			dispatch(
				getChapterContent({
					courseId,
					chapterId,
					opts: {noNotificationOnError: true},
				})
			);
		}
	}, [chapterId, courseId, dispatch, shouldRequestContent]);

	return (
		<Table sx={{tableLayout: "fixed", width: 1}}>
			<TableBody>
				{contentFetchStatus === "failed" && (
					<LoadingErrorState
						columnNumber={columnNumber}
						description={
							<Localized id="content-issues-widget-error-fetch-chapter-content">
								Something has gone wrong, and we cannot load chapter content
							</Localized>
						}
						onReload={() =>
							dispatch(
								getChapterContent({
									courseId,
									chapterId,
									opts: {noNotificationOnError: true},
								})
							)
						}
					/>
				)}

				{sectionKeys.map((key) => (
					<SectionIssuesRow
						key={key}
						chapterPath={props.chapterPath}
						chapterId={chapterId}
						chapterNumber={props.chapterNumber}
						sectionKey={key}
						stats={stats}
						PercentageBar={props.PercentageBar}
						onOpenMessages={props.onOpenMessages}
					/>
				))}
			</TableBody>
		</Table>
	);
}

function SectionIssuesRow(props: {
	chapterPath: string;
	chapterId: number;
	chapterNumber: number;
	sectionKey: string;
	stats: ExerciseContentIssueStats[];

	PercentageBar: React.ComponentType<LinearProgressProps>;

	onOpenMessages: (contentItem: ContentItem) => void;
}) {
	const selectSection = useMemo(makeSelectSection, []);
	const section = useAppSelector((state) =>
		selectSection(state, props.sectionKey)
	);

	const subsectionIds = useAppSelector((state) =>
		selectSubsectionIds(state, props.sectionKey)
	);

	const {l10n} = useLocalization();

	if (!section) {
		return null;
	}

	const sectionProperStats = props.stats.find(
		(s) => s.exerciseId === section.id
	);

	const number = `${props.chapterNumber}.${section.number}`;
	const title = `${number} ${section.title}`;

	const sectionPath = resolvePath(props.chapterPath, `/sections/${section.id}`);

	return (
		<>
			{sectionProperStats && (
				<ReportRow
					contentTitle={
						<ContentItemTitle
							primary={title}
							secondary={
								<Localized id="content-issues-widget-content-item-type-section">
									Section
								</Localized>
							}
							link={sectionPath}
						/>
					}
					numberOfIssues={sectionProperStats.numberOfIssues}
					bar={
						<props.PercentageBar value={sectionProperStats.numberOfIssues} />
					}
					action={
						<IconButtonWithTooltip
							aria-label={l10n.getString(
								"content-issues-widget-action-open-issue-view"
							)}
							tooltipTitle={
								<Localized id="content-issues-widget-action-open-issue-view">
									Open issues
								</Localized>
							}
							onClick={() =>
								props.onOpenMessages({
									id: section.id,
									chapterId: section.chapterId,
									contentPath: sectionPath,
									title: title,
									subtitle: (
										<Localized id="content-issues-widget-content-item-type-section">
											Section
										</Localized>
									),
								})
							}
						>
							<FullscreenIcon />
						</IconButtonWithTooltip>
					}
					indent={4}
				/>
			)}

			{subsectionIds.map((id) => {
				const s = props.stats.find((s) => s.exerciseId === id);
				if (!s) {
					return null;
				}

				return (
					<SubsectionIssuesRow
						key={id}
						subsectionKey={keyProvider.chapterExercise(props.chapterId, id)}
						sectionNumber={number}
						sectionPath={sectionPath}
						numberOfIssues={s.numberOfIssues}
						PercentageBar={props.PercentageBar}
						onOpenMessages={props.onOpenMessages}
					/>
				);
			})}
		</>
	);
}

function SubsectionIssuesRow(props: {
	subsectionKey: string;
	sectionNumber: string;
	sectionPath: string;

	numberOfIssues: number;

	PercentageBar: React.ComponentType<LinearProgressProps>;

	onOpenMessages: (contentItem: ContentItem) => void;
}) {
	const exercise = useAppSelector((state) =>
		selectChapterExercise(state, props.subsectionKey)
	);

	const {l10n} = useLocalization();

	const title = `${props.sectionNumber}.${exercise.number} ${exercise.title}`;

	const type = getExerciseTypeDescription(exercise.type);

	const exercisePath = props.sectionPath + `#subsection-${exercise.id}`;

	return (
		<ReportRow
			contentTitle={
				<ContentItemTitle
					primary={title}
					secondary={type.displayName}
					link={exercisePath}
					icon={type.icon}
				/>
			}
			numberOfIssues={props.numberOfIssues}
			bar={<props.PercentageBar value={props.numberOfIssues} />}
			action={
				<IconButtonWithTooltip
					tooltipTitle={
						<Localized id="content-issues-widget-action-open-issue-view">
							Open issues
						</Localized>
					}
					aria-label={l10n.getString(
						"content-issues-widget-action-open-issue-view"
					)}
					onClick={() =>
						props.onOpenMessages({
							id: exercise.id,
							chapterId: exercise.chapterId,
							contentPath: exercisePath,
							title: title,
							subtitle: type.displayName,
						})
					}
				>
					<FullscreenIcon />
				</IconButtonWithTooltip>
			}
			indent={4}
		/>
	);
}

function ReportRow(props: {
	contentTitle: React.ReactNode;
	numberOfIssues: React.ReactNode;
	bar: React.ReactNode;
	action: React.ReactNode;
	indent?: number;
}) {
	return (
		<TableRow hover>
			<TableCell def={columnDefs.contentItem} sx={{pl: props.indent}}>
				{props.contentTitle}
			</TableCell>
			<TableCell def={columnDefs.issues}>{props.numberOfIssues}</TableCell>
			<TableCell def={columnDefs.bar}>{props.bar}</TableCell>
			<TableCell def={actionColumn}>{props.action}</TableCell>
		</TableRow>
	);
}

function ReportRowPlaceholder(props: {
	indentation?: number;
	title?: React.ReactNode;
}) {
	return (
		<TableRow>
			<TableCell def={columnDefs.contentItem} sx={{pl: props.indentation}}>
				{props.title ? props.title : <ContentItemTitlePlaceholder />}
			</TableCell>
			<TableCell def={columnDefs.issues}>
				<Box sx={{display: "flex", justifyContent: "right"}}>
					<Typography variant="inherit" component="span">
						<Skeleton width="3ch" sx={{maxWidth: "100%"}} />
					</Typography>
				</Box>
			</TableCell>
			<TableCell def={columnDefs.bar}>
				<Skeleton
					variant="rectangular"
					width={`${randomN(10, 100)}%`}
					sx={{height: (theme) => theme.spacing(1)}}
				/>
			</TableCell>
			<TableCell def={actionColumn} />
		</TableRow>
	);
}

function createPercentageBar(max: number) {
	return function PercentageBar(props: LinearProgressProps) {
		return (
			<LinearProgress
				variant="determinate"
				value={((props.value ?? 0) * 100) / max}
				sx={{
					height: (theme) => theme.spacing(1),
					backgroundColor: "unset",
				}}
			/>
		);
	};
}

export default ContentIssuesWidget;
